import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "@/AuthContext";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Upload, FileText, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function KbisRequiredModal() {
  const { user, refreshUser } = useAuth();
  const required = user?.entreprise?.kbis_required && !user?.entreprise?.kbis_storage_path;
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(false);
  const inputRef = useRef(null);

  // Poll /auth/me silencieusement quand modal affiché : si admin lève kbis_required,
  // le chef récupère son accès sans devoir se reconnecter.
  useEffect(() => {
    if (!required) return;
    const id = setInterval(() => { refreshUser().catch(() => {}); }, 30000);
    const onFocus = () => { refreshUser().catch(() => {}); };
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [required, refreshUser]);

  if (!required) return null;

  const submit = async () => {
    if (!file) { toast.error("Sélectionnez un fichier"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/chef/entreprise/kbis", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("KBIS reçu — merci !");
      await refreshUser();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const checkNow = async () => {
    setChecking(true);
    try { await refreshUser(); toast.success("Statut rafraîchi"); }
    catch { toast.error("Impossible de rafraîchir"); }
    finally { setChecking(false); }
  };

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-lg [&>button.absolute]:hidden" data-testid="kbis-required-modal" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-[#FFD700]" /> Document obligatoire requis
          </DialogTitle>
          <DialogDescription className="text-sm">
            Pour finaliser votre compte PAG, merci de déposer votre extrait <b>KBIS</b> (ou pièce d'activité). Sans ce document, l'accès aux fonctionnalités est limité.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-4 bg-[#F8F9FA] border border-[#DEE2E6] text-xs space-y-1">
            <div className="flex items-center gap-1.5 font-semibold"><FileText className="h-3.5 w-3.5" /> Formats acceptés</div>
            <div className="text-[#495057]">PDF, JPG ou PNG — max 10 Mo</div>
          </div>

          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,image/png,image/jpeg"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="hidden"
            data-testid="kbis-file-input"
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            data-testid="kbis-pick-btn"
            className="w-full p-6 border-2 border-dashed border-[#DEE2E6] hover:border-[#002FA7] hover:bg-[#002FA7]/5 transition-colors text-center cursor-pointer"
          >
            <Upload className="h-6 w-6 mx-auto mb-2 text-[#002FA7]" />
            {file ? (
              <div>
                <div className="font-semibold text-sm">{file.name}</div>
                <div className="text-xs text-[#868E96] mt-1">{(file.size / 1024 / 1024).toFixed(2)} Mo</div>
              </div>
            ) : (
              <div className="text-sm font-semibold">Cliquez pour sélectionner un fichier</div>
            )}
          </button>

          <Button onClick={submit} disabled={!file || submitting} data-testid="kbis-submit-btn" className="w-full h-12 bg-[#002FA7] text-white font-semibold">
            {submitting ? "Envoi en cours…" : "Envoyer mon KBIS"}
          </Button>

          <button
            type="button"
            onClick={checkNow}
            disabled={checking}
            data-testid="kbis-recheck-btn"
            className="w-full text-[11px] uppercase tracking-[0.2em] font-bold text-[#002FA7] hover:underline disabled:opacity-50 flex items-center justify-center gap-1.5 py-1"
          >
            <RefreshCw className={`h-3 w-3 ${checking ? "animate-spin" : ""}`} />
            {checking ? "Vérification…" : "Mon admin a déjà validé ? Rafraîchir"}
          </button>

          <div className="text-[10px] text-[#868E96] text-center">
            Besoin d'aide ? <a href="mailto:contact@parcavenue.eu" className="text-[#002FA7] font-semibold">contact@parcavenue.eu</a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
